import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ArrowUpRight, ArrowRight, Menu, X } from 'lucide-react';

export const Navbar = ({ onOpenBriefing }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();

  const goToCompany = (hash = '') => {
    navigate(`/company${hash}`);
    setOpenDropdown(null);
    setMobileOpen(false);
  };

  const goToProducts = (hash = '') => {
    navigate(`/products${hash}`);
    setOpenDropdown(null);
    setMobileOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const textColor = scrolled ? 'text-slate-600' : 'text-white/80';
  const textHover = scrolled ? 'hover:text-slate-900' : 'hover:text-white';
  const logoColor = scrolled ? 'text-[#0F172A]' : 'text-white';
  const chevronColor = scrolled ? 'text-slate-400' : 'text-white/50';
  const dropdownBg = scrolled ? 'bg-[#090E1A]/95 backdrop-blur-2xl' : 'bg-[#090E1A]/75 backdrop-blur-2xl';

  const handleBriefing = (type) => {
    if (onOpenBriefing) onOpenBriefing(type);
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setOpenDropdown(null);
    setMobileOpen(false);
  };

  const scrollToPractice = (index) => {
    const el = document.getElementById('practices');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    window.dispatchEvent(new CustomEvent('frontier:activate-pillar', { detail: index }));
    setOpenDropdown(null);
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 lg:px-12 flex items-center justify-between h-16">
        {/* Left nav */}
        <div className="hidden lg:flex items-center gap-10">

          {/* Products Dropdown */}
          <div className="relative" onMouseEnter={() => setOpenDropdown('products')} onMouseLeave={() => setOpenDropdown(null)}>
            <button onClick={() => goToProducts()} className={`flex items-center gap-1 text-[13px] font-medium ${textColor} ${textHover} transition-colors py-4`}>
              Products <ChevronDown size={14} className={`${chevronColor} transition-transform ${openDropdown === 'products' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'products' && (
              <div className="absolute top-full -left-6 pt-2 w-[520px] z-50">
                <div className={`${dropdownBg} border border-white/15 rounded-2xl shadow-2xl p-6 text-white`}>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-semibold block mb-4">The OS Platform</span>
                      <div className="space-y-3">
                        <button onClick={() => goToProducts('#top')} className="group/item block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="flex items-center gap-2 font-semibold text-white transition text-[13px]">
                            Deal OS™
                            <span className="text-[10px] bg-white/15 text-white/70 px-1.5 py-0.5 rounded font-medium">Core</span>
                          </div>
                          <p className="text-[12px] text-white/45 mt-0.5">Cognitive deal engine runtime</p>
                        </button>
                        <button onClick={() => goToProducts('#workflows')} className="group/item block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white transition text-[13px]">Workflows</div>
                          <p className="text-[12px] text-white/45 mt-0.5">End-to-end autonomous deal pipelines</p>
                        </button>
                        <button onClick={() => goToProducts('#brain')} className="group/item block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white transition text-[13px]">Brain</div>
                          <p className="text-[12px] text-white/45 mt-0.5">Unified transaction memory & cross-deal intelligence</p>
                        </button>
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-semibold block mb-4">Dealmaker Products</span>
                      <div className="space-y-2.5">
                        <button onClick={() => goToProducts('#studio')} className="group/item block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white transition text-[13px]">Studio</div>
                          <p className="text-[12px] text-white/45">Unified dealmaker cockpit & IC deliberations</p>
                        </button>
                        <button onClick={() => goToProducts('#memo')} className="group/item block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white transition text-[13px]">Memo</div>
                          <p className="text-[12px] text-white/45">Audit-grade strategic decks & IC memos</p>
                        </button>
                        <button onClick={() => goToProducts('#model')} className="group/item block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white transition text-[13px]">Model</div>
                          <p className="text-[12px] text-white/45">Deterministic off-LLM valuation & return models</p>
                        </button>
                        <button onClick={() => goToProducts('#chat')} className="group/item block w-full text-left hover:bg-white/10 p-2.5 rounded-xl transition">
                          <div className="font-semibold text-white transition text-[13px]">Chat</div>
                          <p className="text-[12px] text-white/45">Cell-grounded VDR data room interrogation</p>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <button onClick={() => goToProducts('#top')} className="flex items-center gap-2 text-[13px] font-semibold text-white hover:text-white/70 transition px-2.5">
                      Explore Interactive 3D Stack <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Solutions Dropdown */}
          <div className="relative" onMouseEnter={() => setOpenDropdown('solutions')} onMouseLeave={() => setOpenDropdown(null)}>
            <button onClick={() => scrollTo('lifecycle')} className={`flex items-center gap-1 text-[13px] font-medium ${textColor} ${textHover} transition-colors py-4`}>
              Deals & Practices <ChevronDown size={14} className={`${chevronColor} transition-transform ${openDropdown === 'solutions' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'solutions' && (
              <div className="absolute top-full -left-12 pt-2 w-[520px] z-50">
                <div className={`${dropdownBg} border border-white/15 rounded-2xl shadow-2xl p-6 text-white`}>
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-semibold block mb-4">By stage</span>
                      <ul className="space-y-2.5 text-[14px]">
                        <li><button onClick={() => scrollTo('lifecycle')} className="block text-left text-white hover:text-white/70 transition font-medium">Sourcing</button></li>
                        <li><button onClick={() => scrollTo('lifecycle')} className="block text-left text-white hover:text-white/70 transition font-medium">Diligence</button></li>
                        <li><button onClick={() => scrollTo('lifecycle')} className="block text-left text-white hover:text-white/70 transition font-medium">Value creation</button></li>
                        <li><button onClick={() => scrollTo('lifecycle')} className="block text-left text-white hover:text-white/70 transition font-medium">Strategic exit</button></li>
                      </ul>
                    </div>
                    <div>
                      <span className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-semibold block mb-4">By practice</span>
                      <ul className="space-y-2.5 text-[14px]">
                        <li><button onClick={() => scrollToPractice(0)} className="block text-left text-white hover:text-white/70 transition font-medium">Venture capital</button></li>
                        <li><button onClick={() => scrollToPractice(1)} className="block text-left text-white hover:text-white/70 transition font-medium">Private equity</button></li>
                        <li><button onClick={() => scrollToPractice(2)} className="block text-left text-white hover:text-white/70 transition font-medium">Corporate M&A</button></li>
                        <li><button onClick={() => scrollToPractice(3)} className="block text-left text-white hover:text-white/70 transition font-medium">Strategy advisory</button></li>
                        <li><button onClick={() => scrollToPractice(4)} className="block text-left text-white hover:text-white/70 transition font-medium">Investment banking</button></li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <button onClick={() => scrollTo('engage')} className="flex items-center gap-2 text-[13px] font-semibold text-white hover:text-white/70 transition">
                      Deploy the platform. Or deliver the mandate <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <button onClick={() => scrollTo('sovereignty')} className={`text-[13px] font-medium ${textColor} ${textHover} transition-colors`}>
            Sovereignty
          </button>
        </div>

        {/* Center logo */}
        <div className="flex items-center justify-center lg:absolute lg:left-1/2 lg:-translate-x-1/2">
          <button onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className={`text-[22px] font-bold tracking-[0.35em] ${logoColor} uppercase transition-colors`} style={{ fontFamily: "'Inter', sans-serif" }}>
            Frontier
          </button>
        </div>

        {/* Right nav */}
        <div className="hidden lg:flex items-center gap-10">
          {/* Company Dropdown */}
          <div className="relative" onMouseEnter={() => setOpenDropdown('company')} onMouseLeave={() => setOpenDropdown(null)}>
            <button onClick={() => goToCompany()} className={`flex items-center gap-1 text-[13px] font-medium ${textColor} ${textHover} transition-colors py-4`}>
              Company <ChevronDown size={14} className={`${chevronColor} transition-transform ${openDropdown === 'company' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'company' && (
              <div className="absolute top-full right-0 pt-2 w-[440px] z-50">
                <div className={`${dropdownBg} border border-white/15 rounded-2xl shadow-2xl p-4 text-white grid grid-cols-2 gap-4`}>
                  {/* Left: nav links */}
                  <div className="flex flex-col justify-center gap-3 pl-1">
                    <button onClick={() => goToCompany('#about')} className="text-left text-[14px] font-medium text-white/90 hover:text-white transition">About</button>
                    <button onClick={() => goToCompany('#leadership')} className="text-left text-[14px] font-medium text-white/90 hover:text-white transition">Leadership</button>
                    <button onClick={() => goToCompany('#systems')} className="text-left text-[14px] font-medium text-white/90 hover:text-white transition">News</button>
                    <button onClick={() => goToCompany('#footprint')} className="text-left text-[14px] font-medium text-white/90 hover:text-white transition">Footprint</button>
                    <button onClick={() => goToCompany('#footprint')} className="text-left text-[14px] font-semibold text-white hover:text-white/70 transition">Contact</button>
                  </div>
                  {/* Right: featured dispatch card */}
                  <button onClick={() => goToCompany('#systems')} className="text-left rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-3 transition group">
                    <div className="rounded-lg bg-[#060A13] border border-white/10 h-16 flex items-center justify-center mb-2.5">
                      <div className="flex items-center gap-1">
                        <span className="font-extrabold text-[13px] text-white tracking-tight">IdeaSpring</span>
                        <span className="font-light tracking-widest text-[13px] text-white/50">CAPITAL</span>
                      </div>
                    </div>
                    <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-white/50 block mb-1">Featured Dispatch</span>
                    <p className="text-[12px] font-semibold text-white leading-snug">Automating deep-tech PortCo monitoring &amp; exit positioning</p>
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => handleBriefing('co-design')}
            className={`flex items-center gap-2 text-[13px] font-semibold rounded-full px-5 py-2 transition-all ${
              scrolled
                ? 'text-[#0F172A] border border-[#0F172A] hover:bg-[#090E1A] hover:text-white'
                : 'text-white border border-white/60 hover:bg-white hover:text-[#090E1A]'
            }`}
          >
            Request Briefing <ArrowUpRight size={14} />
          </button>
        </div>

        {/* Mobile menu toggle */}
        <button
          className={`lg:hidden p-2 ${scrolled ? 'text-slate-700' : 'text-white'}`}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-6 flex flex-col gap-4 shadow-xl max-h-[80vh] overflow-y-auto">
          <button onClick={() => goToProducts()} className="text-left text-sm font-medium text-slate-700 py-2 border-b border-slate-100">Products</button>
          <button onClick={() => scrollTo('lifecycle')} className="text-left text-sm font-medium text-slate-700 py-2 border-b border-slate-100">Deals & Practices</button>
          <button onClick={() => scrollTo('sovereignty')} className="text-left text-sm font-medium text-slate-700 py-2 border-b border-slate-100">Sovereignty</button>
          <button onClick={() => goToCompany()} className="text-left text-sm font-medium text-slate-700 py-2 border-b border-slate-100">Company</button>
          <button
            onClick={() => { handleBriefing('co-design'); setMobileOpen(false); }}
            className="mt-2 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-[#090E1A] rounded-full px-6 py-3"
          >
            Request Briefing <ArrowUpRight size={14} />
          </button>
        </div>
      )}
    </nav>
  );
};
